/**
 * Centralized Socket.IO Event Names
 *
 * This file defines ALL Socket.IO event names used throughout the application.
 * Using constants ensures consistency between frontend and backend.
 *
 * NAMING CONVENTION:
 * - Use SCREAMING_SNAKE_CASE for clarity and visibility
 * - Format: <ENTITY>_<ACTION>
 * - Examples: ORDER_NEW, KOT_UPDATED, TABLE_STATUS_CHANGED
 *
 * DO NOT use colon notation (order:new) - use underscores (ORDER_NEW)
 * Reason: More readable, easier to grep, consistent with common event naming
 */

// ============================================================================
// ORDER EVENTS
// ============================================================================

export const ORDER_EVENTS = {
  /** Emitted when a new order is created */
  NEW: 'ORDER_NEW',

  /** Emitted when an order status changes (PENDING → ACCEPTED → PREPARING, etc.) */
  STATUS_UPDATE: 'ORDER_STATUS_UPDATE',

  /** Emitted when an order is updated (items added/removed) */
  UPDATED: 'ORDER_UPDATED',

  /** Emitted when an order is cancelled */
  CANCELLED: 'ORDER_CANCELLED',

  /** Emitted when an order is completed */
  COMPLETED: 'ORDER_COMPLETED',
} as const;

// ============================================================================
// KOT (Kitchen Order Ticket) EVENTS
// ============================================================================

export const KOT_EVENTS = {
  /** Emitted when a new KOT is created */
  NEW: 'KOT_NEW',

  /** Emitted when a KOT status changes */
  STATUS_UPDATE: 'KOT_STATUS_UPDATE',

  /** Emitted when a KOT is updated */
  UPDATED: 'KOT_UPDATED',

  /** Emitted when a KOT is printed */
  PRINTED: 'KOT_PRINTED',
} as const;

// ============================================================================
// TABLE EVENTS
// ============================================================================

export const TABLE_EVENTS = {
  /** Emitted when a table status changes (available → occupied) */
  STATUS_CHANGED: 'TABLE_STATUS_CHANGED',

  /** Emitted when a table is assigned to a steward */
  ASSIGNED: 'TABLE_ASSIGNED',

  /** Emitted when a table is updated (capacity, position, etc.) */
  UPDATED: 'TABLE_UPDATED',

  /** Emitted when orders are transferred from one table to another */
  TRANSFER: 'TABLE_TRANSFER',
} as const;

// ============================================================================
// VENDOR EVENTS
// ============================================================================

export const VENDOR_EVENTS = {
  /** Emitted for general vendor data updates */
  UPDATE: 'VENDOR_UPDATE',

  /** Emitted when vendor settings change */
  SETTINGS_CHANGED: 'VENDOR_SETTINGS_CHANGED',
} as const;

// ============================================================================
// KITCHEN EVENTS
// ============================================================================

export const KITCHEN_EVENTS = {
  /** Emitted when kitchen data is updated */
  UPDATE: 'KITCHEN_UPDATE',

  /** Emitted when a new item is assigned to a kitchen */
  ITEM_ASSIGNED: 'KITCHEN_ITEM_ASSIGNED',
} as const;

// ============================================================================
// NOTIFICATION EVENTS
// ============================================================================

export const NOTIFICATION_EVENTS = {
  /** Generic notification event */
  GENERAL: 'NOTIFICATION',

  /** Emitted for system-wide announcements */
  ANNOUNCEMENT: 'ANNOUNCEMENT',
} as const;

// ============================================================================
// BILLING EVENTS
// ============================================================================

export const BILLING_EVENTS = {
  /** Emitted when a bill is requested by customer/steward */
  REQUESTED: 'BILL_REQUESTED',

  /** Emitted when a bill is settled */
  SETTLED: 'BILL_SETTLED',
} as const;

// ============================================================================
// FINANCE EVENTS
// ============================================================================

export const FINANCE_EVENTS = {
  /** Emitted to vendor room when a new finance alert is created. Payload: { id, title, severity, alertType, unreadCount } */
  ALERT_NEW: 'FINANCE_ALERT_NEW',

  /** Emitted when unread alert count changes (read/dismiss/mark-all). Payload: { unreadCount } */
  ALERT_UNREAD_COUNT: 'FINANCE_ALERT_UNREAD_COUNT',
} as const;

// ============================================================================
// PRINTING EVENTS
// ============================================================================

export const PRINTING_EVENTS = {
  /** Emitted when a customer receipt should be printed (triggers desktop/mobile print service) */
  PRINT_RECEIPT: 'PRINT_RECEIPT',
} as const;

// ============================================================================
// RESERVATION EVENTS
// ============================================================================

export const RESERVATION_EVENTS = {
  /** Emitted when a new reservation is created */
  NEW: 'RESERVATION_NEW',

  /** Emitted when a reservation is updated */
  UPDATED: 'RESERVATION_UPDATED',

  /** Emitted when a reservation is cancelled */
  CANCELLED: 'RESERVATION_CANCELLED',

  /** Emitted when a customer is seated for their reservation */
  SEATED: 'RESERVATION_SEATED',
} as const;

// ============================================================================
// TABLE GROUP (MERGE) EVENTS
// ============================================================================

export const TABLE_GROUP_EVENTS = {
  /** Emitted when a table group is created (tables merged) */
  CREATED: 'TABLE_GROUP_CREATED',

  /** Emitted when a table group is released (tables unmerged) */
  CLOSED: 'TABLE_GROUP_CLOSED',
} as const;

// ============================================================================
// WAITLIST EVENTS
// ============================================================================

export const WAITLIST_EVENTS = {
  /** Emitted when a customer is added to the waitlist */
  ADDED: 'WAITLIST_ADDED',

  /** Emitted when a waitlist customer is notified that their table is ready */
  NOTIFIED: 'WAITLIST_NOTIFIED',

  /** Emitted when a waitlist customer is seated */
  SEATED: 'WAITLIST_SEATED',

  /** Emitted when a customer is removed from the waitlist */
  REMOVED: 'WAITLIST_REMOVED',
} as const;

// ============================================================================
// CONNECTION EVENTS (Built-in Socket.IO events)
// ============================================================================

export const CONNECTION_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  RECONNECT: 'reconnect',
} as const;

// ============================================================================
// SUBSCRIPTION EVENTS (Client → Server)
// ============================================================================

export const SUBSCRIPTION_EVENTS = {
  /** Subscribe to order updates */
  SUBSCRIBE_ORDER: 'subscribe:order',
  UNSUBSCRIBE_ORDER: 'unsubscribe:order',

  /** Subscribe to kitchen updates */
  SUBSCRIBE_KITCHEN: 'subscribe:kitchen',
  UNSUBSCRIBE_KITCHEN: 'unsubscribe:kitchen',

  /** Subscribe to vendor updates */
  SUBSCRIBE_VENDOR: 'subscribe:vendor',
  UNSUBSCRIBE_VENDOR: 'unsubscribe:vendor',

  /** Subscribe to table updates */
  SUBSCRIBE_TABLE: 'subscribe:table',
  UNSUBSCRIBE_TABLE: 'unsubscribe:table',
} as const;

// ============================================================================
// ALL EVENTS (Flattened for easy import)
// ============================================================================

export const SOCKET_EVENTS = {
  ...ORDER_EVENTS,
  ...KOT_EVENTS,
  ...TABLE_EVENTS,
  ...VENDOR_EVENTS,
  ...KITCHEN_EVENTS,
  ...NOTIFICATION_EVENTS,
  ...BILLING_EVENTS,
  ...PRINTING_EVENTS,
  ...RESERVATION_EVENTS,
  ...TABLE_GROUP_EVENTS,
  ...WAITLIST_EVENTS,
  ...CONNECTION_EVENTS,
  ...SUBSCRIPTION_EVENTS,
} as const;

// ============================================================================
// TYPE EXPORTS (for TypeScript type safety)
// ============================================================================

export type OrderEventType = typeof ORDER_EVENTS[keyof typeof ORDER_EVENTS];
export type KotEventType = typeof KOT_EVENTS[keyof typeof KOT_EVENTS];
export type TableEventType = typeof TABLE_EVENTS[keyof typeof TABLE_EVENTS];
export type VendorEventType = typeof VENDOR_EVENTS[keyof typeof VENDOR_EVENTS];
export type KitchenEventType = typeof KITCHEN_EVENTS[keyof typeof KITCHEN_EVENTS];
export type NotificationEventType = typeof NOTIFICATION_EVENTS[keyof typeof NOTIFICATION_EVENTS];
export type BillingEventType = typeof BILLING_EVENTS[keyof typeof BILLING_EVENTS];
export type PrintingEventType = typeof PRINTING_EVENTS[keyof typeof PRINTING_EVENTS];
export type ReservationEventType = typeof RESERVATION_EVENTS[keyof typeof RESERVATION_EVENTS];
export type TableGroupEventType = typeof TABLE_GROUP_EVENTS[keyof typeof TABLE_GROUP_EVENTS];
export type WaitlistEventType = typeof WAITLIST_EVENTS[keyof typeof WAITLIST_EVENTS];
export type ConnectionEventType = typeof CONNECTION_EVENTS[keyof typeof CONNECTION_EVENTS];
export type SubscriptionEventType = typeof SUBSCRIPTION_EVENTS[keyof typeof SUBSCRIPTION_EVENTS];

export type SocketEventType =
  | OrderEventType
  | KotEventType
  | TableEventType
  | VendorEventType
  | KitchenEventType
  | NotificationEventType
  | BillingEventType
  | PrintingEventType
  | ReservationEventType
  | TableGroupEventType
  | WaitlistEventType
  | ConnectionEventType
  | SubscriptionEventType;

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/*
// In a React component:
import { SOCKET_EVENTS } from '@/constants/socketEvents';

useEffect(() => {
  socket.on(SOCKET_EVENTS.ORDER_NEW, handleNewOrder);
  socket.on(SOCKET_EVENTS.ORDER_STATUS_UPDATE, handleOrderUpdate);

  return () => {
    socket.off(SOCKET_EVENTS.ORDER_NEW, handleNewOrder);
    socket.off(SOCKET_EVENTS.ORDER_STATUS_UPDATE, handleOrderUpdate);
  };
}, []);

// In backend (Java):
// Create a corresponding SocketEvents.java class with the same constants
public class SocketEvents {
    public static final String ORDER_NEW = "ORDER_NEW";
    public static final String ORDER_STATUS_UPDATE = "ORDER_STATUS_UPDATE";
    // ... etc
}

// Then emit events:
socketIOServer.getRoomOperations("vendor_" + vendorId)
    .sendEvent(SocketEvents.ORDER_NEW, orderData);
*/

export default SOCKET_EVENTS;
